import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  BusyState, FailedInstance, HistoryEntry, ScheduleDetail, ScheduleForm, ScheduleNotice, ScheduleRecord, ScheduleRun,
} from "../models/schedule.model";
import { fmtDate, fmtDateTime, fmtTime } from "../schedule.utils";

type Tab = "servers" | "run" | "notices" | "teams" | "history";

const STATUS_CLASS: Record<string, string> = {
  Scheduled: "pill-info",
  "Deviation requested": "pill-warn",
  Rescheduled: "pill-info-soft",
  Completed: "pill-ok",
  "Completed with issues": "pill-warn",
  Failed: "pill-err",
  Cancelled: "pill-muted",
};

/** Presentational: shows one schedule and emits what the user wants to do. */
@Component({
  selector: "app-schedule-detail",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./schedule-detail.component.css"],
  templateUrl: "./schedule-detail.component.html",
})
export class ScheduleDetailComponent implements OnInit {
  @Input() detail!: ScheduleDetail;
  @Input() busy: BusyState = "idle";

  @Output() back = new EventEmitter<void>();
  @Output() editRequest = new EventEmitter<void>();
  @Output() cancelRequest = new EventEmitter<void>();
  @Output() checkRequest = new EventEmitter<void>();

  tab: Tab = "servers";

  ngOnInit(): void {
    if (this.run) {
      this.tab = "run";
    }
  }

  // ---- Safe accessors -------------------------------------------------

  get form(): ScheduleForm {
    return this.detail.form;
  }

  get record(): ScheduleRecord {
    return this.detail.schedule;
  }

  get run(): ScheduleRun | null {
    return this.record?.run || null;
  }

  get serverList(): any[] {
    return this.form?.servers || [];
  }

  get noticeList(): ScheduleNotice[] {
    return this.form?.notices || [];
  }

  get teamList(): any[] {
    return this.form?.teams || [];
  }

  get historyList(): HistoryEntry[] {
    return this.record?.history || [];
  }

  get runInstances(): any[] {
    return (this.run as any)?.instances || [];
  }

  get runReports(): string[] {
    return (this.run as any)?.reports || [];
  }

  get runGroupErrors(): string[] {
    return (this.run as any)?.group_errors || [];
  }

  /** Uses the backend summary when present, otherwise computes it from the instances. */
  get summary(): { instances: number; succeeded: number; not_succeeded: number } {
    const s = (this.run as any)?.summary;
    const list = this.runInstances;
    const total = s?.instances ?? list.length;
    const succeeded = s?.succeeded ?? list.filter((i: any) => i.patch_status == "Success").length;
    return {
      instances: total,
      succeeded,
      not_succeeded: s?.not_succeeded ?? total - succeeded,
    };
  }

  // ---- View state -----------------------------------------------------

  get editable(): boolean {
    return !!this.form?.editable;
  }

  get statusClass(): string {
    return STATUS_CLASS[this.form?.status] || "pill-info-soft";
  }

  get movedFrom(): string {
    const m = this.form?.moved_from;
    return m ? fmtDate(m) || m : "";
  }

  get failureReason(): string {
    return this.record?.failure_reason || this.form?.failure_reason || (this.run as any)?.error || "";
  }

  get failedInstances(): FailedInstance[] {
    return this.record?.failed_instances || this.form?.failed_instances || [];
  }

  get removedLabels(): string[] {
    return (this.form?.removed_instances || []).map((r: any) =>
      typeof r == "string" ? r : r.instance_id || r.name || ""
    );
  }

  get tabs(): { id: Tab; label: string; count?: number }[] {
    const t: { id: Tab; label: string; count?: number }[] = [
      { id: "servers", label: "Servers", count: this.serverList.length },
    ];
    if (this.run) {
      t.push({ id: "run", label: "Run results" });
    }
    t.push(
      { id: "notices", label: "Notices", count: this.noticeList.length },
      { id: "teams", label: "Teams", count: this.teamList.length },
      { id: "history", label: "History", count: this.historyList.length }
    );
    return t;
  }

  setTab(t: Tab): void {
    this.tab = t;
  }

  // ---- Helpers --------------------------------------------------------

  noticeClass(n: ScheduleNotice): string {
    return n.status == "Sent" ? "pill-ok" : n.status == "Missed" ? "pill-warn" : "pill-info";
  }

  patchClass(status: string): string {
    return status == "Success" ? "pill-ok" : status == "Not run" ? "pill-muted" : "pill-err";
  }

  /** "2026-10-13T12:15+05:30" -> "13 Oct, 12:15" (as written, in the schedule's timezone) */
  at(iso?: string | null): string {
    return iso ? `${fmtDate(iso)}, ${fmtTime(iso)}` : "\u2014";
  }

  /** UTC timestamp -> IST */
  utc(iso?: string | null): string {
    return fmtDateTime(iso || "") || "\u2014";
  }

  fileName(path: string): string {
    return (path || "").split("/").pop() || path;
  }

  join(list?: string[] | null): string {
    return list && list.length ? list.join(", ") : "\u2014";
  }

  count(list?: any[] | null): number {
    return list ? list.length : 0;
  }

  rebootText(): string {
    return this.form?.reboot_option == "RebootIfNeeded" ? "Reboot if needed" : this.form?.reboot_option || "\u2014";
  }

  trackByIndex(i: number) {
    return i;
  }

  trackHistory(i: number, h: HistoryEntry) {
    return `${h.at}-${i}`;
  }
}